import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import NodeContentEditorModal from './NodeContentEditorModal';
import WorkflowAiPanel from './WorkflowAiPanel';
import FormDefinitionPreview from './FormDefinitionPreview';
import FormInput from '../../../shared/components/FormInput';
import IconButton from '../../../shared/components/IconButton';
import PhotoReferenceImageField from '../media-library/PhotoReferenceImageField';
import useForms from '../../hooks/useForms';
import WorkflowHtmlPreview from './WorkflowHtmlPreview';
import { buildEdgeId } from '../../utils/flowEdgeUtils';

const SIDEBAR_WIDTH = 392;

function normalizedNodeType(type) {
  return (type || 'default').toString().toLowerCase();
}

function nodeShowsInstructionEditor(nodeType) {
  return nodeType === 'html' || nodeType === 'default' || nodeType === 'photo' || !nodeType;
}

const shellStyle = {
  width: SIDEBAR_WIDTH,
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
};

const EditorSidebar = forwardRef(function EditorSidebar(
  {
    workflow,
    onWorkflowChange,
    selectedItem,
    setSelectedItem,
    clearLastClickedNode,
    saveWorkflow,
    onAiStepsApplied,
    stepValidationMap,
    edgeValidationMap,
  },
  ref
) {
  const [formData, setFormData] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formDetailLoading, setFormDetailLoading] = useState(false);
  const { forms, fetchFormBySlug } = useForms();

  const selectedValidation = (() => {
    if (!selectedItem) return null;
    if (selectedItem.type === 'node') {
      return stepValidationMap?.get(selectedItem.id) || null;
    }
    if (selectedItem.type === 'edge') {
      const edgeKey =
        selectedItem.edgeId ||
        (typeof selectedItem.actionIndex === 'number'
          ? buildEdgeId(selectedItem.source, selectedItem.target, selectedItem.actionIndex)
          : `${selectedItem.source}->${selectedItem.target}`);
      return edgeValidationMap?.get(edgeKey) || null;
    }
    return null;
  })();

  const selectedValidationMessages = selectedValidation
    ? [...(selectedValidation.errors || []), ...(selectedValidation.warnings || [])]
    : [];

  useEffect(() => {
    let cancelled = false;

    if (!selectedItem || !workflow?.workflow_steps) {
      setFormData(null);
      setSelectedForm(null);
      setFormDetailLoading(false);
      return undefined;
    }

    if (selectedItem.type === 'node') {
      const view = workflow.workflow_steps.find((v) => v.id === selectedItem.id);
      if (view) {
        const nodeType = normalizedNodeType(view.type);
        setFormData({
          type: 'node',
          id: view.id,
          heading: view.heading || '',
          text: view.text || '',
          nodeType,
          slug: view.slug || '',
          label: view.label || '',
          graphic: view.graphic || '',
          graphic_media_id: view.graphic_media_id || null,
          required: view.required !== false,
        });

        if (nodeType === 'form' && view.slug) {
          setSelectedForm(null);
          setFormDetailLoading(true);
          fetchFormBySlug(view.slug)
            .then((form) => {
              if (!cancelled) {
                setSelectedForm(form || null);
              }
            })
            .catch((err) => {
              console.error('Error fetching form:', err);
              if (!cancelled) {
                setSelectedForm(null);
              }
            })
            .finally(() => {
              if (!cancelled) {
                setFormDetailLoading(false);
              }
            });
        } else {
          setSelectedForm(null);
          setFormDetailLoading(false);
        }
      }
    } else if (selectedItem.type === 'edge') {
      const sourceView = workflow.workflow_steps.find((v) => v.id === selectedItem.source);
      const navAction =
        typeof selectedItem.actionIndex === 'number'
          ? sourceView?.actions?.[selectedItem.actionIndex]
          : sourceView?.actions?.find(
              (a) => a.type === 'navigation' && a.link === selectedItem.target
            );
      if (sourceView && navAction?.type === 'navigation') {
        setFormData({
          type: 'edge',
          source: selectedItem.source,
          target: selectedItem.target,
          actionIndex: selectedItem.actionIndex,
          text: navAction.text || '',
        });
      } else {
        setFormData(null);
      }
      setSelectedForm(null);
      setFormDetailLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [selectedItem, workflow, fetchFormBySlug]);

  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    clearLastClickedNode();
  }, [setSelectedItem, clearLastClickedNode]);

  const commitDeselectAndSave = useCallback(() => {
    if (!workflow?.workflow_steps) {
      clearSelection();
      return;
    }

    if (!formData) {
      clearSelection();
      return;
    }

    let updatedWorkflow = workflow;
    if (formData.type === 'node') {
      const updatedSteps = workflow.workflow_steps.map((node) => {
        if (node.id !== formData.id) return node;

        const patch = {
          ...node,
          heading: formData.heading,
          text: formData.text,
        };

        if (formData.nodeType === 'form') {
          patch.slug = formData.slug;
        }

        if (formData.nodeType === 'photo') {
          patch.label = formData.label;
          patch.graphic = formData.graphic || '';
          patch.graphic_media_id = formData.graphic_media_id || null;
          if (patch.graphic_media_id) {
            patch.graphic = '';
          }
          patch.required = formData.required !== false;
        }

        return patch;
      });
      updatedWorkflow = { ...workflow, workflow_steps: updatedSteps };
      onWorkflowChange(updatedWorkflow, formData);
    } else if (formData.type === 'edge') {
      const updatedSteps = workflow.workflow_steps.map((node) => {
        if (node.id !== formData.source) return node;
        const updatedActions = (node.actions || []).map((action, index) =>
          index === formData.actionIndex ? { ...action, text: formData.text } : action
        );
        return { ...node, actions: updatedActions };
      });
      updatedWorkflow = { ...workflow, workflow_steps: updatedSteps };
      onWorkflowChange(updatedWorkflow, formData);
    }

    clearSelection();

    if (saveWorkflow) {
      saveWorkflow(updatedWorkflow);
    }
  }, [workflow, formData, onWorkflowChange, clearSelection, saveWorkflow]);

  useImperativeHandle(
    ref,
    () => ({
      commitDeselectAndSave,
    }),
    [commitDeselectAndSave]
  );

  const handleSave = () => {
    commitDeselectAndSave();
  };

  const handleContentSave = (newContent) => {
    setFormData((prev) => ({ ...prev, text: newContent }));
    setShowContentModal(false);
  };

  const handleFormSelection = (formSlug) => {
    setFormData((prev) => ({ ...prev, slug: formSlug }));
    if (formSlug) {
      setSelectedForm(null);
      setFormDetailLoading(true);
      fetchFormBySlug(formSlug)
        .then((form) => {
          setSelectedForm(form || null);
        })
        .catch((err) => {
          console.error('Error fetching selected form:', err);
          setSelectedForm(null);
        })
        .finally(() => {
          setFormDetailLoading(false);
        });
    } else {
      setSelectedForm(null);
      setFormDetailLoading(false);
    }
  };

  if (!workflow) {
    return (
      <div className="bg-white border-start d-flex flex-column" style={shellStyle}>
        <div className="text-center text-muted p-3 m-auto">
          <i className="bi bi-diagram-3" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3 mb-0">No workflow loaded</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <WorkflowAiPanel
        workflowId={workflow.id}
        workflowSteps={workflow.workflow_steps}
        onStepsApplied={onAiStepsApplied}
      />
    );
  }

  return (
    <div className="bg-white border-start d-flex flex-column" style={shellStyle}>
      <div className="px-3 pt-3 pb-2 flex-shrink-0 border-bottom bg-white">
        {formData.type === 'node' ? (
          <>
            <div className="small text-muted font-monospace text-break mb-1">
              Step ID: {formData.id}
            </div>
            <h5 className="fw-semibold mb-0">Edit step</h5>
          </>
        ) : (
          <>
            <div className="small text-muted font-monospace text-break mb-1">
              {formData.source} <span className="text-muted px-1">→</span> {formData.target}
            </div>
            <h5 className="fw-semibold mb-0">Edit connection</h5>
          </>
        )}
      </div>

      <div className="flex-grow-1 overflow-auto px-3 py-3" style={{ minHeight: 0 }}>
        {selectedValidationMessages.length > 0 && (
          <div
            className={`alert py-2 px-3 small mb-3 ${
              selectedValidation?.errors?.length ? 'alert-danger' : 'alert-warning'
            }`}
            role="status"
          >
            <div className="fw-semibold mb-1">Validation issues</div>
            <ul className="mb-0 ps-3">
              {selectedValidationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        {formData.type === 'node' && (
          <>
            <FormInput
              id="editor-node-heading"
              name="heading"
              label="Heading"
              value={formData.heading}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, heading: e.target.value }))
              }
              placeholder="Step title shown in the workflow"
              className="mb-2"
            />

            {formData.nodeType === 'form' && (
              <>
                <label className="form-label form-label-caps">Form</label>
                <select
                  value={formData.slug || ''}
                  onChange={(e) => handleFormSelection(e.target.value)}
                  className="form-select mb-2"
                >
                  <option value="">Select a form...</option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.slug}>
                      {form.title} ({form.slug})
                    </option>
                  ))}
                </select>

                {formDetailLoading && (
                  <div className="text-muted small py-3 d-flex align-items-center gap-2">
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Loading form…
                  </div>
                )}

                {!formDetailLoading && selectedForm && (
                  <div className="mb-3">
                    <div className="fw-medium">{selectedForm.title}</div>
                    {selectedForm.description && (
                      <div className="small text-muted mb-2">{selectedForm.description}</div>
                    )}
                    <label className="form-label form-label-caps">Preview</label>
                    <FormDefinitionPreview
                      schema={selectedForm.schema}
                      uiSchema={selectedForm.uiSchema}
                      defaults={selectedForm.defaults}
                    />
                  </div>
                )}
              </>
            )}

            {formData.nodeType === 'photo' && (
              <>
                <FormInput
                  id="editor-node-photo-label"
                  name="label"
                  label="Report label"
                  value={formData.label}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, label: e.target.value }))
                  }
                  placeholder="Name shown in task reports and admin media views"
                  helperText="Copied to the capture record when the field engineer uploads a photo."
                  className="mb-2"
                />
                <PhotoReferenceImageField
                  graphicMediaId={formData.graphic_media_id}
                  graphicUrl={formData.graphic}
                  onChange={({ graphic_media_id, graphic }) =>
                    setFormData((prev) => ({
                      ...prev,
                      graphic_media_id,
                      graphic,
                    }))
                  }
                />
                <div className="form-check mb-3">
                  <input
                    id="editor-node-photo-required"
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.required !== false}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, required: e.target.checked }))
                    }
                  />
                  <label className="form-check-label" htmlFor="editor-node-photo-required">
                    Photo required before continuing
                  </label>
                </div>
              </>
            )}

            {nodeShowsInstructionEditor(formData.nodeType) && (
              <>
                <input type="hidden" name="text" value={formData.text} />
                <label className="form-label form-label-caps">
                  {formData.nodeType === 'photo' ? 'Instructions' : 'Content preview'}
                </label>
                <div
                  className="form-control mb-2 workflow-html-preview-shell"
                  style={{ minHeight: 120, overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                >
                  <WorkflowHtmlPreview
                    className="workflow-html-preview"
                    html={formData.text}
                  />
                </div>
                <IconButton
                  type="button"
                  className="btn btn-outline-primary w-100"
                  icon="bi-pencil-square"
                  iconPosition="start"
                  onClick={() => setShowContentModal(true)}
                >
                  {formData.nodeType === 'photo' ? 'Edit instructions' : 'Edit content'}
                </IconButton>
                <NodeContentEditorModal
                  show={showContentModal}
                  onClose={() => setShowContentModal(false)}
                  initialValue={formData.text}
                  onSave={handleContentSave}
                />
              </>
            )}
          </>
        )}

        {formData.type === 'edge' && (
          <FormInput
            id="editor-edge-transition-label"
            name="text"
            label="Transition label"
            value={formData.text}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, text: e.target.value }))
            }
            placeholder="Button / link label"
            helperText="Shown on the button that moves the user along this connection."
          />
        )}
      </div>

      <div className="flex-shrink-0 bg-white border-top shadow-sm p-3 d-grid gap-2">
        <IconButton
          type="button"
          onClick={handleSave}
          className="btn btn-success w-100"
          icon="bi-check-lg"
          iconPosition="start"
        >
          Save
        </IconButton>
        <IconButton
          type="button"
          onClick={() => {
            setSelectedItem(null);
            clearLastClickedNode();
          }}
          className="btn btn-outline-secondary w-100"
        >
          Cancel
        </IconButton>
      </div>
    </div>
  );
});

export default EditorSidebar;
